import { ref, Ref } from "vue";
import { Swiper } from "swiper/types";

// Swiper has some issues with duplicate slides and screen readers/automation.
// This function hides the duplicate slides from screen readers and Playwright.
function hideDuplicateSlidesFromScreenReaders(swiper: Swiper) {
  const duplicateSlides = Array.from(swiper.slides).filter((slide) =>
    (slide as HTMLElement).classList.contains("swiper-slide-duplicate")
  );

  duplicateSlides.forEach((slide) => {
    const element = slide as HTMLElement;
    element.setAttribute("aria-hidden", "true");
    element.removeAttribute("data-testid");
  });
}

// There's an issue in Swiper where the autoplay will stop if the component
// is remounted or if the key changes. This function restarts the autoplay.
function restartAutoplayIfStopped(swiper: Swiper) {
  if (!swiper || !swiper.params?.autoplay) {
    return;
  }

  // @todo: Upgrade to Swiper 10 or 11, which hopefully fixes this issue.
  if (
    swiper &&
    (swiper.autoplay?.running === false || swiper.autoplay?.paused === true)
  ) {
    setTimeout(() => {
      try {
        swiper?.autoplay?.start();
      } catch (error) {
        console.error("Error starting autoplay", error);
      }
    }, 1000);
  }
}

function onSwiperInstanceSet(swiper: Swiper) {
  hideDuplicateSlidesFromScreenReaders(swiper);
  restartAutoplayIfStopped(swiper);
}

export function useSwiperInstance() {
  const swiperInstanceRef: Ref<Swiper | null> = ref(null);

  const setSwiperInstance = (
    swiper: Swiper,
    afterSwiperInstanceSet?: () => void
  ) => {
    swiperInstanceRef.value = swiper;

    onSwiperInstanceSet(swiper);

    if (afterSwiperInstanceSet) {
      afterSwiperInstanceSet();
    }
  };

  return { swiperInstanceRef, setSwiperInstance };
}
